import { useEffect, useMemo, useState, useCallback } from "react";
import { api, fmtNumber, fmtRupiah, fmtDate } from "@/lib/api";
import { KpiCard } from "@/components/KpiCard";
import { StatusBadge } from "@/components/StatusBadge";
import { DeptBar, TopSuppliers, LeadTimeProcess } from "@/components/Charts";
import { DateRangePicker } from "@/components/DateRangePicker";
import { format } from "date-fns";
import { DeptAmountChart } from "@/components/DeptAmountChart";
import { SlaSettingsDialog } from "@/components/SlaSettingsDialog";
import { LoginDialog, SessionBadge } from "@/components/LoginDialog";
import { setUnlockHandler } from "@/lib/api";
import { POForm } from "@/components/POForm";
import { ImportDialog, ExportDialog } from "@/components/ImportExportDialogs";
import { DetailDrawer } from "@/components/DetailDrawer";
import { AlertsPopover } from "@/components/AlertsPopover";
import { BulkStatusBar } from "@/components/BulkStatusBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Package, AlertTriangle, CheckCircle2, Clock, XCircle, Plus, Upload, Download, Search, Pencil, Trash2, RefreshCw, Wallet, Truck, Hourglass, FileText, FileDown, Settings, Armchair } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [options, setOptions] = useState({ cost_centers: [], departments: [], suppliers: [], statuses: [] });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCC, setFilterCC] = useState("all");
  const [filterDept, setFilterDept] = useState("all");
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [slaOpen, setSlaOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const isAdmin = () => !!localStorage.getItem("access_token");
  const requireAdmin = (action) => {
    if (isAdmin()) return action();
    toast.info("Sign in to edit data");
    setAdminOpen(true);
  };

  useEffect(() => { setUnlockHandler(() => setAdminOpen(true)); return () => setUnlockHandler(null); }, []);
  const [detailRow, setDetailRow] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState({}); // id -> true
  const [alertRefresh, setAlertRefresh] = useState(0);
  const [dateRange, setDateRange] = useState(undefined);
  const dateParams = useMemo(() => (dateRange?.from && dateRange?.to
    ? { date_from: format(dateRange.from, "yyyy-MM-dd"), date_to: format(dateRange.to, "yyyy-MM-dd") } : {}), [dateRange]);

  const loadStats = useCallback(async () => {
    try { const r = await api.get("/dashboard/stats", { params: dateParams }); setStats(r.data); } catch (e) { /* ignore */ }
  }, [dateParams]);
  const loadOptions = useCallback(async () => {
    try { const r = await api.get("/filters/options"); setOptions(r.data); } catch (e) { /* ignore */ }
  }, []);
  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize, search, ...dateParams };
      if (filterStatus !== "all") params.status = filterStatus;
      if (filterCC !== "all") params.cost_center = filterCC;
      if (filterDept !== "all") params.department = filterDept;
      const r = await api.get("/purchases", { params });
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
    } catch (e) { toast.error("Failed to load data"); }
    finally { setLoading(false); }
  }, [page, pageSize, search, filterStatus, filterCC, filterDept, dateParams]);

  useEffect(() => { loadStats(); loadOptions(); }, [loadStats, loadOptions]);
  useEffect(() => { loadRows(); }, [loadRows]);

  const refresh = () => { loadStats(); loadOptions(); loadRows(); setAlertRefresh((n) => n + 1); };

  const openDetail = (row) => { setDetailRow(row); setDrawerOpen(true); };
  const openDetailById = async (id) => {
    const local = rows.find((r) => r.id === id);
    if (local) return openDetail(local);
    try {
      const r = await api.get("/purchases", { params: { search: id, page: 1, page_size: 1 } });
      if (r.data.rows?.[0]) openDetail(r.data.rows[0]);
    } catch (e) { /* ignore */ }
  };

  const onDelete = async (id, e) => {
    e?.stopPropagation();
    if (!window.confirm("Delete this record?")) return;
    try { await api.delete(`/purchases/${id}`); toast.success("Record deleted"); refresh(); }
    catch (err) { toast.error("Failed to delete"); }
  };

  const seed = async () => {
    if (!window.confirm("Reload sample data? Current data will be replaced.")) return;
    try { const r = await api.post("/purchases/seed"); toast.success(`${r.data.imported} records loaded`); refresh(); }
    catch (err) { toast.error("Failed to load sample"); }
  };

  const toggleAll = (checked) => {
    if (checked) { const next = {}; rows.forEach((r) => (next[r.id] = true)); setSelected(next); }
    else setSelected({});
  };
  const toggleOne = (id, checked) => setSelected((prev) => { const n = { ...prev }; if (checked) n[id] = true; else delete n[id]; return n; });
  const selectedIds = useMemo(() => Object.keys(selected), [selected]);
  const allSelected = rows.length > 0 && rows.every((r) => selected[r.id]);
  const someSelected = selectedIds.length > 0 && !allSelected;

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const kpi = stats?.kpi;

  return (
    <div className="min-h-screen bg-[#0B132B] bg-grid-pattern text-slate-100">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#0B132B]/85 border-b border-slate-800/80">
        <div className="max-w-[1700px] mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-12 px-3 py-1.5 rounded-xl bg-white flex items-center justify-center shadow-lg shadow-slate-950/40 ring-1 ring-white/10">
              <img src="/prima-logo.png" alt="PRIMA" className="h-9 w-auto object-contain" data-testid="company-logo" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-amber-400 font-semibold">Purchase-Track Pro</div>
              <h1 className="text-lg sm:text-xl font-extrabold tracking-tight text-slate-50">Purchasing Monitoring System</h1>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-2">
            <AlertsPopover onOpenDetail={openDetailById} refreshToken={alertRefresh} onChanged={refresh} requireAdmin={requireAdmin} />
            <SessionBadge onLoginClick={() => setAdminOpen(true)} />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => requireAdmin(() => setSlaOpen(true))}
              data-testid="btn-sla-settings"
              className="text-slate-300 hover:text-amber-300 gap-1.5"
              title="SLA Target Settings"
            >
              <Settings className="h-4 w-4" /> SLA
            </Button>
            <Button variant="ghost" size="sm" onClick={() => requireAdmin(seed)} data-testid="btn-reload-seed" className="text-slate-300 hover:text-amber-300 gap-1.5">
              <RefreshCw className="h-4 w-4" /> Reload Sample
            </Button>
            <DateRangePicker value={dateRange} onChange={(r) => { setDateRange(r); setPage(1); }} />
            <Button size="sm" onClick={() => requireAdmin(() => setImportOpen(true))} data-testid="btn-open-import" className="bg-slate-700 hover:bg-slate-600 text-slate-100 gap-1.5">
              <Upload className="h-4 w-4" /> Import Excel
            </Button>
            <Button size="sm" onClick={() => setExportOpen(true)} data-testid="btn-open-export" className="bg-slate-700 hover:bg-slate-600 text-slate-100 gap-1.5">
              <Download className="h-4 w-4" /> Export Excel
            </Button>
            <Button
              size="sm"
              onClick={() => {
                window.open(`${process.env.REACT_APP_BACKEND_URL}/api/reports/daily-pdf`, "_blank");
                toast.success("Downloading PDF report");
              }}
              data-testid="btn-download-pdf"
              className="bg-rose-600/90 hover:bg-rose-600 text-white gap-1.5"
            >
              <FileDown className="h-4 w-4" /> PDF Report
            </Button>
            <Button size="sm" onClick={() => requireAdmin(() => { setEditing(null); setFormOpen(true); })} data-testid="btn-open-add" className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold gap-1.5">
              <Plus className="h-4 w-4" /> Add PO
            </Button>
          </div>
        </div>
      </header>

      <div className="md:hidden px-4 pt-4 flex justify-end"><DateRangePicker value={dateRange} onChange={(r) => { setDateRange(r); setPage(1); }} /></div>
      <main className="max-w-[1700px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4">
          <KpiCard testId="kpi-total-po" icon={Package} tone="amber" label="Total PO" unit="item" value={fmtNumber(kpi?.total_po)} sub={`${kpi?.open_count || 0} open · ${kpi?.close_count || 0} closed`} />
          <KpiCard testId="kpi-full-supply" icon={CheckCircle2} tone="emerald" label="Full Supply" unit="item" value={fmtNumber(kpi?.full_supply_count)} sub={`${kpi?.full_supply_rate || 0}% completion rate`} />
          <KpiCard testId="kpi-waiting-supply" icon={Hourglass} tone="amber" label="Waiting Supply" unit="item" value={fmtNumber(kpi?.waiting_supply_count)} sub="PO shared, awaiting delivery" />
          <KpiCard testId="kpi-waiting-po" icon={FileText} tone="sky" label="Waiting PO" unit="item" value={fmtNumber(kpi?.waiting_po_count)} sub="Quotation → PO not shared" />
          <KpiCard testId="kpi-waiting-quot" icon={Clock} tone="sky" label="Waiting Quot" unit="item" value={fmtNumber(kpi?.waiting_quot_count)} sub="PR awaiting quotation" />
          <KpiCard testId="kpi-waiting-spp" icon={Armchair} tone="rose" label="Waiting SPP" unit="item" value={fmtNumber(kpi?.waiting_spp_count)} sub="Asset > Rp 2 jt (non-SLA)" />
          <KpiCard testId="kpi-cancel" icon={XCircle} tone="slate" label="Cancel" unit="item" value={fmtNumber(kpi?.cancel_count)} sub="PO cancelled" />
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          <div className="lg:col-span-12"><DeptBar data={stats?.by_department || []} /></div>
          <div className="lg:col-span-12"><DeptAmountChart data={stats?.by_department || []} /></div>
          <div className="lg:col-span-6"><TopSuppliers data={stats?.top_suppliers || []} /></div>
          <div className="lg:col-span-6"><LeadTimeProcess data={stats?.lead_time_process} /></div>
        </section>

        <section className="rounded-xl border border-slate-700/60 bg-[#111D38]/70 backdrop-blur-md overflow-hidden">
          <div className="p-4 border-b border-slate-700/60 flex flex-col md:flex-row items-stretch md:items-center gap-3">
            <h3 className="text-slate-100 font-semibold text-base flex-shrink-0 mr-2">Purchase Order Data</h3>
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <Input data-testid="input-search" placeholder="Search PO No, Part No, Description, Supplier..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="bg-[#0B132B] border-slate-700 pl-9 h-9 text-sm" />
            </div>
            <div className="flex gap-2 flex-wrap">
              <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v); setPage(1); }}>
                <SelectTrigger data-testid="filter-status" className="bg-[#0B132B] border-slate-700 h-9 w-40 text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent className="bg-[#1C2541] border-slate-700 text-slate-100">
                  <SelectItem value="all">All Status</SelectItem>
                  {options.statuses.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterDept} onValueChange={(v) => { setFilterDept(v); setPage(1); }}>
                <SelectTrigger data-testid="filter-dept" className="bg-[#0B132B] border-slate-700 h-9 w-40 text-xs"><SelectValue placeholder="Dept" /></SelectTrigger>
                <SelectContent className="bg-[#1C2541] border-slate-700 text-slate-100 max-h-72">
                  <SelectItem value="all">All Departments</SelectItem>
                  {options.departments.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterCC} onValueChange={(v) => { setFilterCC(v); setPage(1); }}>
                <SelectTrigger data-testid="filter-cc" className="bg-[#0B132B] border-slate-700 h-9 w-44 text-xs"><SelectValue placeholder="Cost Center" /></SelectTrigger>
                <SelectContent className="bg-[#1C2541] border-slate-700 text-slate-100 max-h-72">
                  <SelectItem value="all">All Cost Centers</SelectItem>
                  {options.cost_centers.slice(0, 100).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 bg-[#0B132B]/50">
                  <th className="px-3 py-3 w-10">
                    <Checkbox
                      data-testid="checkbox-select-all"
                      checked={allSelected ? true : someSelected ? "indeterminate" : false}
                      onCheckedChange={toggleAll}
                      className="border-slate-500 data-[state=checked]:bg-amber-500 data-[state=checked]:text-slate-900 data-[state=checked]:border-amber-500"
                    />
                  </th>
                  <th className="px-3 py-3 font-semibold">PR / PO No</th>
                  <th className="px-3 py-3 font-semibold">Part No</th>
                  <th className="px-3 py-3 font-semibold">Description</th>
                  <th className="px-3 py-3 font-semibold text-right">Qty</th>
                  <th className="px-3 py-3 font-semibold">UoM</th>
                  <th className="px-3 py-3 font-semibold">Supplier</th>
                  <th className="px-3 py-3 font-semibold">Cost Center</th>
                  <th className="px-3 py-3 font-semibold">Status</th>
                  <th className="px-3 py-3 font-semibold"><span className="flex items-center gap-1"><Truck className="h-3 w-3" />ETA</span></th>
                  <th className="px-3 py-3 font-semibold text-right">Lead Time</th>
                  <th className="px-3 py-3 font-semibold">PR Date</th>
                  <th className="px-3 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody data-testid="po-table-body">
                {loading && (<tr><td colSpan={13} className="px-4 py-16 text-center text-slate-400">Loading data...</td></tr>)}
                {!loading && rows.length === 0 && (<tr><td colSpan={13} className="px-4 py-16 text-center text-slate-400">No data available</td></tr>)}
                {!loading && rows.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => openDetail(r)}
                    data-testid={`row-${r.id}`}
                    className={`border-t border-slate-800/70 hover:bg-amber-500/[0.05] cursor-pointer transition-colors ${selected[r.id] ? "bg-amber-500/[0.06]" : ""}`}
                  >
                    <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        data-testid={`checkbox-${r.id}`}
                        checked={!!selected[r.id]}
                        onCheckedChange={(v) => toggleOne(r.id, v)}
                        className="border-slate-500 data-[state=checked]:bg-amber-500 data-[state=checked]:text-slate-900 data-[state=checked]:border-amber-500"
                      />
                    </td>
                    <td className="px-3 py-3 mono text-xs">
                      <div className="text-amber-300 font-semibold">{r.po_number || "-"}</div>
                      <div className="text-slate-500">{r.pr_number || r.doc_number}</div>
                    </td>
                    <td className="px-3 py-3 mono text-xs text-slate-200">{r.product_no || "-"}</td>
                    <td className="px-3 py-3 text-slate-200 max-w-[240px] truncate" title={r.description}>{r.description || "-"}</td>
                    <td className="px-3 py-3 text-right mono text-slate-100">{fmtNumber(r.doc_qty)}</td>
                    <td className="px-3 py-3 text-slate-300">{r.uom}</td>
                    <td className="px-3 py-3 text-slate-300 max-w-[180px] truncate" title={r.supplier}>{r.supplier || "-"}</td>
                    <td className="px-3 py-3 text-slate-300 max-w-[160px] truncate" title={r.cost_center}>{r.cost_center || r.department || "-"}</td>
                    <td className="px-3 py-3"><StatusBadge value={r.status_key} /></td>
                    <td className="px-3 py-3 text-xs">
                      {r.eta_date ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-sky-500/10 text-sky-300 border border-sky-500/30 mono">
                          {fmtDate(r.eta_date)}
                        </span>
                      ) : <span className="text-slate-600">-</span>}
                    </td>
                    <td className="px-3 py-3 text-right mono text-slate-100">{fmtNumber(r.aging_pr_gr)} days</td>
                    <td className="px-3 py-3 text-slate-400 text-xs">{fmtDate(r.pr_date)}</td>
                    <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <button data-testid={`btn-edit-${r.id}`} onClick={() => requireAdmin(() => { setEditing(r); setFormOpen(true); })} className="p-1.5 rounded-md text-slate-400 hover:text-amber-300 hover:bg-amber-500/10">
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button data-testid={`btn-delete-${r.id}`} onClick={(e) => { e.stopPropagation(); requireAdmin(() => onDelete(r.id)); }} className="p-1.5 rounded-md text-slate-400 hover:text-rose-300 hover:bg-rose-500/10">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="p-4 border-t border-slate-700/60 flex items-center justify-between text-sm">
            <div className="text-slate-400 text-xs">Showing <span className="text-slate-200 font-semibold">{rows.length}</span> of <span className="text-slate-200 font-semibold">{fmtNumber(total)}</span> records</div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="btn-prev" className="text-slate-300 hover:text-amber-300 disabled:opacity-40">← Prev</Button>
              <span className="text-xs text-slate-400">Page <span className="text-slate-100 font-semibold">{page}</span> / {totalPages}</span>
              <Button size="sm" variant="ghost" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="btn-next" className="text-slate-300 hover:text-amber-300 disabled:opacity-40">Next →</Button>
            </div>
          </div>
        </section>

        <footer className="pt-2 pb-8 text-center text-[11px] text-slate-500">
          Purchase-Track Pro × <span className="text-slate-300 font-semibold">PRIMA</span> · Modern purchasing monitoring system · Built for the procurement team
        </footer>
      </main>

      <BulkStatusBar
        selectedIds={selectedIds}
        onClear={() => setSelected({})}
        onDone={() => { setSelected({}); refresh(); }}
      />

      <POForm open={formOpen} onOpenChange={setFormOpen} initial={editing} onSaved={refresh} />
      <ImportDialog open={importOpen} onOpenChange={setImportOpen} onImported={refresh} />
      <ExportDialog open={exportOpen} onOpenChange={setExportOpen} />
      <DetailDrawer row={detailRow} open={drawerOpen} onOpenChange={setDrawerOpen} />
      <SlaSettingsDialog open={slaOpen} onOpenChange={setSlaOpen} onSaved={refresh} />
      <LoginDialog open={adminOpen} onOpenChange={setAdminOpen} onSuccess={refresh} />
    </div>
  );
}
